import { ReactNode } from 'react'

import { useWindowResize } from '@utils/domUtils'

import { PlatformInfoDto } from '@model/PlatformInfoDto'
import {
  OperatingSystemEnum,
  PlatformTypeEnum,
  detectAndroidOperatingSystem,
  detectIOSOperatingSystem,
  detectLinuxOperatingSystem,
  detectMacOSOperatingSystem,
  detectWindowsOperatingSystem,
} from '@model/PlatformTypeEnum'

import { AppStoreBanner } from '@assets/banners/AppStore.banner'
import { GooglePlayBanner } from '@assets/banners/GooglePlay.banner'
import { LinuxBanner } from '@assets/banners/Linux.banner'
import { MacOSBanner } from '@assets/banners/MacOS.banner'
import { WebAppBanner } from '@assets/banners/WebApp.banner'
import { WindowsBanner } from '@assets/banners/Windows.banner'

type PlatformDownloadLink = {
  icon: ReactNode
  link: string
}

type PlatformTradingViewDownloadLinksData = {
  platformDownloadLinksData: PlatformDownloadLink[]
}

interface LinkData {
  os: OperatingSystemEnum
  icon: ReactNode
  condition?: () => boolean
  desktopCondition?: () => boolean
  link: string
  enabled: boolean
}

const platformDownloadLinksData: Omit<LinkData, 'link'>[] = [
  {
    os: OperatingSystemEnum.LINUX,
    icon: <LinuxBanner width={125} height={40} />,
    condition: detectLinuxOperatingSystem,
    desktopCondition: detectLinuxOperatingSystem,
    enabled: true,
  },
  {
    os: OperatingSystemEnum.WINDOWS,
    icon: <WindowsBanner width={153} height={40} />,
    condition: detectWindowsOperatingSystem,
    desktopCondition: detectWindowsOperatingSystem,
    enabled: true,
  },
  {
    os: OperatingSystemEnum.OSX,
    icon: <MacOSBanner width={153} height={40} />,
    condition: detectMacOSOperatingSystem,
    desktopCondition: detectMacOSOperatingSystem,
    enabled: true,
  },
  {
    os: OperatingSystemEnum.IOS,
    icon: <AppStoreBanner width={119} height={40} />,
    condition: detectIOSOperatingSystem,
    enabled: true,
  },
  {
    os: OperatingSystemEnum.ANDROID,
    icon: <GooglePlayBanner width={135} height={40} />,
    condition: detectAndroidOperatingSystem,
    enabled: true,
  },

  {
    os: OperatingSystemEnum.WEB_TRADER,
    icon: <WebAppBanner />,
    condition: () => detectAndroidOperatingSystem() || detectIOSOperatingSystem(),
    enabled: true,
  },
]

export const usePlatformTradingViewDownloadLinksData = (
  platformInfo: PlatformInfoDto[]
): PlatformTradingViewDownloadLinksData => {
  const isMobile = useWindowResize()
  const platformData = platformInfo.find(
    ({ platfromType }) => platfromType.id === PlatformTypeEnum.TradingView
  )

  const parsedData: LinkData[] =
    platformData?.platformLinks?.map((x) => {
      const data = platformDownloadLinksData
        .filter((x) => x.enabled)
        .find((y) => y.os === x.operatingSystem.id)
      return {
        condition: data?.condition,
        desktopCondition: data?.desktopCondition,
        icon: data?.icon,
        link: x.productLink,
        os: x.operatingSystem.id,
        enabled: !!data?.enabled,
      }
    }) || []

  const platformDownloadLinksMobileData = parsedData
    .filter((x) => x.condition?.())
    .map((x) => ({
      icon: x.icon,
      link: x.link,
    }))

  const platformDownloadLinksDesktopData = parsedData
    .filter((x) => x.desktopCondition?.() ?? true)
    .map((x) => ({
      icon: x.icon,
      link: x.link,
    }))

  return {
    platformDownloadLinksData: isMobile
      ? platformDownloadLinksMobileData
      : platformDownloadLinksDesktopData,
  }
}
